import { useMemo, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';
import { roleHint, roleLabel, roleOrder, type RoleInfo } from './Staff.tsx';

/**
 * Roles: what each role may do, the business's own roles, and record rules ("people with this
 * role see only their own jobs"). The database keeps the rules, so they hold everywhere.
 */

interface Rule {
  model: string;
  field: string;
}
interface Role extends RoleInfo {
  rules: Rule[];
  people: number;
}
interface Group {
  module: string;
  label: string;
  permissions: { name: string; label: string; owner_only: boolean; yours: boolean }[];
}
interface Target {
  model: string;
  label: string;
  module: string;
  read: string | null;
  fields: { name: string; label: string }[];
}
interface Data {
  roles: Role[];
  groups: Group[];
  targets: Target[];
  owner: boolean;
}

export function Roles() {
  const { data, reload } = useAction<Data>('staff.roles');
  const [editing, setEditing] = useState<{ role: Role | null; copyOf?: Role } | null>(null);
  const [rulesOf, setRulesOf] = useState<Role | null>(null);
  const own = (data?.roles ?? []).filter((r) => r.custom).sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
  const builtIn = (data?.roles ?? []).filter((r) => !r.custom).sort((a, b) => roleOrder(a.name) - roleOrder(b.name));
  const saved = async () => {
    setEditing(null);
    setRulesOf(null);
    await reload();
  };
  return (
    <ManageLayout
      title={t('roles.title')}
      subtitle={t('roles.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing({ role: null })} disabled={!data}>
          {t('roles.new')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : (
        <div className="roles-page">
          <section className="list-card" aria-labelledby="roles-own">
            <div className="list-card-head">
              <div>
                <h2 id="roles-own">{t('roles.yours')}</h2>
                <p className="muted">{t('roles.yours_hint')}</p>
              </div>
            </div>
            {own.length ? (
              own.map((r) => <RoleRow key={r.name} role={r} roles={data.roles} onOpen={() => setEditing({ role: r })} />)
            ) : (
              <EmptyState
                icon="users"
                title={t('roles.none')}
                body={t('roles.none_body')}
                action={
                  <Button icon="plus" onClick={() => setEditing({ role: null })}>
                    {t('roles.new')}
                  </Button>
                }
              />
            )}
          </section>
          <section className="list-card" aria-labelledby="roles-built-in">
            <div className="list-card-head">
              <div>
                <h2 id="roles-built-in">{t('roles.built_in')}</h2>
                <p className="muted">{t('roles.built_in_hint')}</p>
              </div>
            </div>
            {builtIn.map((r) => (
              <RoleRow key={r.name} role={r} roles={data.roles} onOpen={() => setRulesOf(r)} />
            ))}
          </section>
        </div>
      )}
      {editing && data ? <RoleDialog data={data} role={editing.role} copyOf={editing.copyOf} onClose={() => setEditing(null)} onSaved={saved} /> : null}
      {rulesOf && data ? (
        <BuiltInDialog
          data={data}
          role={rulesOf}
          onClose={() => setRulesOf(null)}
          onSaved={saved}
          onCopy={() => {
            setRulesOf(null);
            setEditing({ role: null, copyOf: rulesOf });
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

function RoleRow({ role: r, roles, onOpen }: { role: Role; roles: Role[]; onOpen: () => void }) {
  return (
    <button type="button" className="list-row role-row" onClick={onOpen}>
      <span className="role-row-icon" aria-hidden="true">
        <Icon name={r.custom ? 'sliders' : 'shield'} size={18} />
      </span>
      <span className="list-main">
        <span className="list-title">
          {roleLabel(r.name, roles)}
          {r.rules.length ? (
            <span className="tag tag-rule">
              <Icon name="lock" size={12} /> {t('roles.own_only')}
            </span>
          ) : null}
        </span>
        <span className="list-sub">{roleHint(r)}</span>
      </span>
      <span className="role-row-meta muted small">
        <span>{tn('roles.permissions_n', r.permissions.length)}</span>
        <span>{tn('roles.people_n', r.people)}</span>
      </span>
      <Icon name="chevronRight" size={18} />
    </button>
  );
}

/** Which kinds of records people with these permissions see only their own of. */
function RulesEditor({ data, perms, rules, onChange }: { data: Data; perms: ReadonlySet<string>; rules: Rule[]; onChange: (r: Rule[]) => void }) {
  const targets = data.targets.filter((x) => !x.read || perms.has(x.read) || rules.some((r) => r.model === x.model));
  if (!targets.length) return <p className="muted small">{t('roles.rules_none')}</p>;
  const set = (model: string, field: string) => onChange([...rules.filter((r) => r.model !== model), ...(field ? [{ model, field }] : [])]);
  return (
    <div className="rules-list">
      {targets.map((x) => {
        const rule = rules.find((r) => r.model === x.model);
        const id = `rule-${x.model}`;
        return (
          <div key={x.model} className={`rule-row${rule ? ' on' : ''}`}>
            <label htmlFor={id} className="rule-kind">
              {rule ? <Icon name="lock" size={14} /> : <Icon name="eye" size={14} />}
              <span>{x.label}</span>
            </label>
            <select id={id} className="input" value={rule?.field ?? ''} onChange={(e) => set(x.model, e.target.value)}>
              <option value="">{t('roles.rule_all')}</option>
              {x.fields.map((f) => (
                <option key={f.name} value={f.name}>
                  {t('roles.rule_theirs', { field: f.label.toLowerCase() })}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
}

function RoleDialog({ data, role, copyOf, onClose, onSaved }: { data: Data; role: Role | null; copyOf?: Role; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const start = role ?? copyOf;
  const [name, setName] = useState(role?.label ?? (copyOf ? t('roles.copy_name', { name: roleLabel(copyOf.name, data.roles) }) : ''));
  const [description, setDescription] = useState(role?.description ?? '');
  // A copy starts with only what the person may give.
  const giveable = useMemo(() => new Set(data.groups.flatMap((g) => g.permissions.filter((p) => p.yours && (data.owner || !p.owner_only)).map((p) => p.name))), [data]);
  const [perms, setPerms] = useState<Set<string>>(() => new Set((start?.permissions ?? []).map((p) => p.name).filter((p) => role || giveable.has(p))));
  const [rules, setRules] = useState<Rule[]>(start?.rules ?? []);
  const [query, setQuery] = useState('');
  const [tried, setTried] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const q = query.trim().toLowerCase();
  const groups = data.groups
    .map((g) => ({ ...g, permissions: g.permissions.filter((p) => !q || p.label.toLowerCase().includes(q) || g.label.toLowerCase().includes(q)) }))
    .filter((g) => g.permissions.length);
  const can = (p: { name: string; yours: boolean; owner_only: boolean }) => perms.has(p.name) || (p.yours && (data.owner || !p.owner_only));
  const toggle = (name: string) =>
    setPerms((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  const toggleGroup = (g: Group, on: boolean) =>
    setPerms((s) => {
      const n = new Set(s);
      for (const p of g.permissions) if (can(p)) on ? n.add(p.name) : n.delete(p.name);
      return n;
    });
  const bad = name.trim().length < 2;
  const save = async () => {
    setTried(true);
    if (bad) return;
    const ok = await run(
      () => action('staff.save_role', { key: role?.name ?? null, name: name.trim(), description: description.trim() || null, permissions: [...perms], rules }),
      role ? t('roles.saved', { name: name.trim() }) : t('roles.created', { name: name.trim() }),
    );
    if (ok) await onSaved();
  };
  return (
    <Dialog
      title={role ? roleLabel(role.name, data.roles) : t('roles.new')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          {role ? (
            <Button variant="ghost" icon="trash" className="dialog-foot-start" onClick={() => setDeleting(true)}>
              {t('roles.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('roles.f_name')} required error={tried && bad ? t('roles.e_name') : null}>
          <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('roles.f_description')} hint={t('roles.f_description_hint')}>
          <input className="input" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>

      <section className="role-section" aria-labelledby="role-perms">
        <div className="role-section-head">
          <div>
            <h3 id="role-perms">{t('roles.what_can_do')}</h3>
            <p className="muted small">{tn('roles.chosen_n', perms.size)}</p>
          </div>
          <div className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" placeholder={t('roles.search')} aria-label={t('roles.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        </div>
        <div className="perm-groups">
          {groups.map((g) => {
            const usable = g.permissions.filter(can);
            const all = usable.length > 0 && usable.every((p) => perms.has(p.name));
            return (
              <fieldset key={g.module} className="perm-group">
                <legend>
                  <label className="perm-group-all">
                    <input type="checkbox" checked={all} disabled={!usable.length} onChange={() => toggleGroup(g, !all)} />
                    <span>{g.label}</span>
                  </label>
                </legend>
                {g.permissions.map((p) => {
                  const allowed = can(p);
                  return (
                    <label key={p.name} className={`perm-item${allowed ? '' : ' off'}`} title={allowed ? undefined : p.owner_only ? t('roles.owner_only_hint') : t('roles.not_yours_hint')}>
                      <input type="checkbox" checked={perms.has(p.name)} disabled={!allowed} onChange={() => toggle(p.name)} />
                      <span>{p.label}</span>
                      {p.owner_only ? <span className="tag tag-owner">{t('roles.owner_only')}</span> : null}
                    </label>
                  );
                })}
              </fieldset>
            );
          })}
          {!groups.length ? <p className="muted">{t('roles.no_match')}</p> : null}
        </div>
      </section>

      <section className="role-section" aria-labelledby="role-rules">
        <h3 id="role-rules">{t('roles.rules')}</h3>
        <p className="muted small">{t('roles.rules_hint')}</p>
        <RulesEditor data={data} perms={perms} rules={rules} onChange={setRules} />
      </section>

      {deleting && role ? (
        <Confirm
          title={t('roles.delete_title', { name: roleLabel(role.name, data.roles) })}
          body={role.people ? tn('roles.delete_in_use', role.people) : t('roles.delete_body')}
          confirm={t('roles.delete')}
          danger
          busy={busy}
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            void run(async () => {
              await action('staff.delete_role', { key: role.name });
              setDeleting(false);
              await onSaved();
            }, t('roles.deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

function BuiltInDialog({ data, role, onClose, onSaved, onCopy }: { data: Data; role: Role; onClose: () => void; onSaved: () => Promise<void>; onCopy: () => void }) {
  const { busy, run } = useSave();
  const [rules, setRules] = useState<Rule[]>(role.rules);
  const perms = useMemo(() => new Set(role.permissions.map((p) => p.name)), [role]);
  const isOwner = role.name === 'owner';
  const save = async () => {
    const ok = await run(() => action('staff.set_rules', { role: role.name, rules }), t('roles.saved', { name: roleLabel(role.name, data.roles) }));
    if (ok) await onSaved();
  };
  return (
    <Dialog
      title={roleLabel(role.name, data.roles)}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" icon="copy" className="dialog-foot-start" onClick={onCopy}>
            {t('roles.copy')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {!isOwner ? (
            <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          ) : null}
        </>
      }
    >
      <p className="muted">{roleHint(role)}</p>
      <section className="role-section" aria-labelledby="builtin-rules">
        <h3 id="builtin-rules">{t('roles.rules')}</h3>
        <p className="muted small">{t('roles.rules_hint')}</p>
        {isOwner ? <p className="muted small">{t('roles.owner_sees_all')}</p> : <RulesEditor data={data} perms={perms} rules={rules} onChange={setRules} />}
      </section>
      <details className="role-perms role-section">
        <summary>{tn('staff.what_role_can_do', role.permissions.length)}</summary>
        <ul>
          {role.permissions.map((p) => (
            <li key={p.name}>{p.label}</li>
          ))}
        </ul>
      </details>
      <p className="callout callout-info small" role="note">
        <Icon name="info" size={16} /> {t('roles.built_in_fixed')}
      </p>
    </Dialog>
  );
}
